import { createSelector } from '@reduxjs/toolkit';
import { createSentinelDataByIdSelector } from '../sentinel/selectors';
import { AppState } from '../types';
import { LocalJobLogData, RemoteJobLogData } from './types';

/** A selector that returns the log feature state. */
const selectLog = (state: AppState) => state.log;

/** A function which creates a new selector that returns job log info for the provided job id. */
export const createLogInfoByIdSelector = (id: string) => createSentinelDataByIdSelector('logs', id);

/** A selector which returns a record of log contents by id, for remote logs only. */
const selectLogRemoteDataById = createSelector(selectLog, state => state.remoteDataById);

/** A function which creates a new selector that returns job log contents for the provided job id. */
export const createLogRemoteDataByIdSelector = (id: string) =>
  createSelector(selectLogRemoteDataById, (data): RemoteJobLogData | undefined => {
    return data[id];
  });

/** A selector which returns a record of log contents by filename, for local logs only. */
export const selectLogLocalDataByFilename = createSelector(selectLog, state => state.localDataByFilename);

/** A selector which returns a list of records that need to prompt the user for a details update */
export const selectLogLocalDataForDetailUpdates = createSelector(
  selectLogLocalDataByFilename,
  (localDataByFilename): LocalJobLogData | undefined =>
    Object.values(localDataByFilename).filter(obj => !obj.didPromptForMetadata)[0],
);

/** A function which creates a new selector that returns job log contents for the provided filename. */
export const createLogLocalDataByFilenameSelector = (filename: string) =>
  createSelector(selectLogLocalDataByFilename, (data): LocalJobLogData | undefined => {
    return data[filename];
  });

/** A selector that returns the currently streaming log filename. */
export const selectLogActiveFileName = createSelector(selectLog, state => state.activeLogFileName);
